import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Check, Users, UsersRound } from 'lucide-react';
import type { Team } from '@tradeoutlet/shared';
import { useTeams } from '@/lib/hooks';

export function TeamIcon({ kind, className = 'h-3.5 w-3.5' }: { kind: Team['kind']; className?: string }) {
  return kind === 'group' ? <UsersRound className={className} /> : <Users className={className} />;
}

/** Chip for a department / group. Group assignment is dynamic, so the tooltip lists current members. */
export function TeamChip({ team, size = 'sm', onRemove }: { team: Team | undefined; size?: 'sm' | 'xs'; onRemove?: () => void }) {
  if (!team) return null;
  const names = (team.members ?? []).map(m => m.display_name).join(', ');
  return (
    <span title={`${team.kind === 'group' ? 'Group' : 'Department'}: ${team.name}${names ? ` - currently ${names}` : ''}`}
      className={clsx('inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 font-medium text-violet-700 dark:border-violet-800 dark:bg-violet-900/30 dark:text-violet-200',
        size === 'xs' ? 'px-1.5 py-px text-[10.5px]' : 'px-2 py-0.5 text-xs')}>
      <TeamIcon kind={team.kind} className={size === 'xs' ? 'h-3 w-3' : 'h-3.5 w-3.5'} />{team.name}
      {team.members && size === 'sm' && <span className="font-normal text-violet-500">· {team.members.length}</span>}
      {onRemove && <button type="button" onClick={onRemove} className="ml-0.5 text-violet-400 hover:text-rose-600">×</button>}
    </span>
  );
}

export function TeamChips({ ids, size = 'sm' }: { ids: string[] | undefined; size?: 'sm' | 'xs' }) {
  const { byId } = useTeams();
  if (!ids?.length) return null;
  return <span className="inline-flex flex-wrap gap-1">{ids.map(id => <TeamChip key={id} team={byId.get(id)} size={size} />)}</span>;
}

/** Multi-select of departments and groups. `helper` is the caller's context-specific note (none by default). */
export function TeamPicker({ selected, onChange, helper }: { selected: string[]; onChange: (ids: string[]) => void; helper?: ReactNode }) {
  const { teams } = useTeams();
  const sections: [string, Team[]][] = [['Departments', teams.filter(t => t.kind !== 'group')], ['Groups', teams.filter(t => t.kind === 'group')]];
  if (!teams.length) return <p className="text-xs text-slate-500">No departments or groups yet.</p>;
  return (
    <div className="space-y-2">
      {sections.filter(([, list]) => list.length).map(([label, list]) => (
        <div key={label}>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
          <div className="flex flex-wrap gap-1.5">
            {list.map(t => {
              const on = selected.includes(t.id);
              return (
                <button key={t.id} type="button" onClick={() => onChange(on ? selected.filter(x => x !== t.id) : [...selected, t.id])}
                  className={clsx('flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs', on ? 'border-violet-400 bg-violet-50 text-violet-800 dark:bg-violet-900/40 dark:text-violet-100' : 'border-slate-200 hover:border-slate-300 dark:border-slate-700')}>
                  {on ? <Check className="h-3 w-3" /> : <TeamIcon kind={t.kind} className="h-3 w-3" />}{t.name}<span className="text-slate-500">{t.members?.length ?? 0}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {helper && <p className="text-xs text-slate-600 dark:text-slate-300" data-testid="team-picker-helper">{helper}</p>}
    </div>
  );
}
